'use client';
import { useState, useEffect, useRef, useCallback } from 'react';

export function useSliderState(blocks: any[], viewport: string) {
  const [activeSlides, setActiveSlides] = useState<Record<string, number>>({});
  // Sliders the visitor paused (WCAG 2.2.2) — the interval keeps running but skips them.
  const [pausedSliders, setPausedSliders] = useState<Record<string, boolean>>({});
  const pausedRef = useRef(pausedSliders);
  pausedRef.current = pausedSliders;
  const toggleSliderPause = useCallback((id: string) => {
    setPausedSliders((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  useEffect(() => {
    const findSliders = (nodes: any[]): any[] => {
      let found: any[] = [];
      for (const node of nodes) {
        if (node.type === 'slider' && node.settings?.autoplay) {
          found.push(node);
        }
        if (node.children) {
          found = [...found, ...findSliders(node.children)];
        }
      }
      return found;
    };

    const autoplaySliders = findSliders(blocks);
    if (autoplaySliders.length === 0) return;
    // Visitors who asked the OS for less motion get a still slider (WCAG 2.3.3).
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    // Rotating hero images keep spawning new Largest Contentful Paint
    // candidates for as long as the browser keeps observing the page (in a
    // Lighthouse lab run that's well past `load` — a fixed delay is a guess
    // against an unknown window and previously still landed the first
    // rotation exactly at the reported LCP). Instead, watch the real LCP
    // entry stream via PerformanceObserver and only start rotating once it
    // has gone quiet (no new candidate for 1s), with a hard cap so a slider
    // never fails to start.
    let intervals: ReturnType<typeof setInterval>[] = [];
    let settleTimeout: ReturnType<typeof setTimeout> | undefined;
    let minDelayTimeout: ReturnType<typeof setTimeout>;
    let maxWaitTimeout: ReturnType<typeof setTimeout>;
    let observer: PerformanceObserver | undefined;
    let started = false;
    let lcpSettled = typeof PerformanceObserver === 'undefined';
    let minDelayElapsed = false;

    const startAutoplay = () => {
      if (started) return;
      started = true;
      clearTimeout(settleTimeout);
      clearTimeout(maxWaitTimeout);
      observer?.disconnect();
      intervals = autoplaySliders.map((slider) => {
        const speed = parseInt(slider.settings?.autoplaySpeed) || 5000;
        return setInterval(() => {
          if (pausedRef.current[slider.id]) return;
          if (slider.children?.length > 1) {
            setActiveSlides((prev) => {
              const currentIndex = prev[slider.id] || 0;
              const nextIndex = (currentIndex + 1) % slider.children.length;
              return { ...prev, [slider.id]: nextIndex };
            });
          }
        }, speed);
      });
    };

    const startWhenSafe = () => {
      if (lcpSettled && minDelayElapsed) startAutoplay();
    };

    // Even when the first LCP candidate settles quickly, keep the initial slide static long
    // enough for lab measurements and slow devices to finish their load. The interval starts
    // only after this gate, so the first actual rotation occurs at 10s + autoplaySpeed.
    minDelayTimeout = setTimeout(() => {
      minDelayElapsed = true;
      startWhenSafe();
    }, 10000);
    // Failsafe for browsers that expose PerformanceObserver but never deliver an LCP entry.
    maxWaitTimeout = setTimeout(startAutoplay, 15000);

    if (typeof PerformanceObserver !== 'undefined') {
      try {
        observer = new PerformanceObserver(() => {
          clearTimeout(settleTimeout);
          settleTimeout = setTimeout(() => {
            lcpSettled = true;
            startWhenSafe();
          }, 1000);
        });
        observer.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch {
        // LCP entry type unsupported in this browser — the fixed minimum delay is sufficient.
        lcpSettled = true;
      }
    }

    return () => {
      clearTimeout(settleTimeout);
      clearTimeout(minDelayTimeout);
      clearTimeout(maxWaitTimeout);
      observer?.disconnect();
      intervals.forEach(clearInterval);
    };
  }, [blocks, viewport]);

  return { activeSlides, setActiveSlides, pausedSliders, toggleSliderPause };
}
